import { useEffect, useMemo, useState } from 'react'
import { FieldRenderer } from './FieldRenderer'
import { useDebouncedSectionUpdate } from '../hooks'
import type { Contract, FieldDefinition, FieldVisibilityDto, FormSchema } from '../types'
import { visibilityMode } from './SectionVisibilityBadge'
import { useFieldVerifications } from '../verification/hooks'
import { SectionFieldVerificationSlot } from '../verification/components/SectionFieldVerificationSlot'
import { SidebarField } from './SidebarField'
import { usePermission } from '@/features/auth/store'
import { FieldDensityProvider } from './field-renderer/FieldDensity'

type Props = {
  contract: Contract
  schema: FormSchema
  sectionCode: string
  visibility: FieldVisibilityDto | undefined
  /** 'sidebar' = kompaktowy render bez karty, pola read-only z click-to-edit. */
  variant?: 'default' | 'sidebar'
}

export function SectionForm({ contract, schema, sectionCode, visibility, variant = 'default' }: Props) {
  const mode = visibilityMode(visibility)
  const updater = useDebouncedSectionUpdate(contract.id, sectionCode)
  const [localValues, setLocalValues] = useState<Record<string, unknown>>(contract.extras)
  const verifications = useFieldVerifications('contract', contract.id)
  const canVerify = usePermission('platform.verification.write')

  useEffect(() => setLocalValues(contract.extras), [contract.extras])

  const verificationsByFieldCode = useMemo(() => {
    const m = new Map<string, NonNullable<typeof verifications.data>['items'][number]>()
    for (const v of verifications.data?.items ?? []) m.set(v.fieldCode, v)
    return m
  }, [verifications.data])

  if (mode === 'HIDDEN') return null

  const section = schema.sections.find(s => s.code === sectionCode)
  if (!section) return <div className="text-sm text-slate-500">Sekcja '{sectionCode}' nie istnieje w schemacie.</div>

  const placed = (schema.placements ?? [])
    .filter(p => p.sectionId === section.id)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const fieldById = new Map(schema.fields.map(f => [f.id, f]))
  // pairs: [field, colWidth per placement]
  const fields: Array<{ field: FieldDefinition; colWidth: number }> = placed
    .map(p => {
      const field = fieldById.get(p.fieldId)
      return field ? { field, colWidth: p.colWidth } : null
    })
    .filter((x): x is { field: FieldDefinition; colWidth: number } => x !== null)

  const handleChange = (fieldCode: string) => (value: unknown) => {
    // Pomiń no-op: wejście w edycję i wyjście bez zmiany (np. onBlur commit pól
    // numerycznych) NIE może generować zapisu - inaczej audit log zbiera śmieci.
    if (valuesEqual(localValues[fieldCode], value)) return
    setLocalValues(prev => ({ ...prev, [fieldCode]: value }))
    if (mode === 'EDIT') updater.queue({ [fieldCode]: value })
  }

  const buildBadge = (f: FieldDefinition) =>
    f.verificationModes.length > 0 ? (
      <SectionFieldVerificationSlot
        field={f}
        subjectType="contract"
        subjectId={contract.id}
        currentValue={stringifyValue(localValues[f.fieldCode])}
        verification={verificationsByFieldCode.get(f.fieldCode) ?? null}
        canVerify={canVerify}
      />
    ) : null

  if (variant === 'sidebar') {
    return (
      <FieldDensityProvider value="compact">
        <div className="px-4 py-1">
          {fields.length === 0 && (
            <p className="text-xs text-slate-400 italic">Brak pól w tej sekcji.</p>
          )}
          <div className="flex flex-col gap-2.5">
            {fields.map(({ field: f }) => (
              <SidebarField
                key={f.fieldCode}
                field={f}
                value={localValues[f.fieldCode]}
                editable={mode === 'EDIT'}
                onChange={handleChange(f.fieldCode)}
                contractId={contract.id}
                verificationBadge={buildBadge(f)}
              />
            ))}
          </div>
          {mode === 'EDIT' && updater.status !== 'idle' && (
            <div className="flex justify-end mt-2 min-h-4">
              <SaveBadge status={updater.status} />
            </div>
          )}
        </div>
      </FieldDensityProvider>
    )
  }

  return (
    <div className="p-4 md:p-6">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-6 py-6">
        {fields.length === 0 && (
          <p className="text-sm text-slate-500 italic">Brak pól w tej sekcji.</p>
        )}
        <div className="grid grid-cols-12 gap-x-6 gap-y-5">
          {fields.map(({ field: f, colWidth }) => {
            const showBadge = f.verificationModes.length > 0
            const verificationBadge = showBadge ? (
              <SectionFieldVerificationSlot
                field={f}
                subjectType="contract"
                subjectId={contract.id}
                currentValue={stringifyValue(localValues[f.fieldCode])}
                verification={verificationsByFieldCode.get(f.fieldCode) ?? null}
                canVerify={canVerify}
              />
            ) : null
            return (
              <div key={f.fieldCode} style={{ gridColumn: `span ${clampWidth(colWidth)}` }}>
                <FieldRenderer
                  field={f}
                  value={localValues[f.fieldCode]}
                  mode={mode}
                  onChange={mode === 'EDIT' ? handleChange(f.fieldCode) : undefined}
                  disabled={mode !== 'EDIT'}
                  contractId={contract.id}
                  verificationBadge={verificationBadge}
                />
              </div>
            )
          })}
        </div>
      </div>
      {mode === 'EDIT' && (
        <div className="flex justify-end mt-2 px-1 min-h-5">
          <SaveBadge status={updater.status} />
        </div>
      )}
    </div>
  )
}

/** True gdy wartości są równe (pomijamy no-op zapisy). null/undefined traktujemy jako równe. */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (typeof a === 'object' || typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  return false
}

function stringifyValue(v: unknown): string | null {
  if (v === null || v === undefined) return null
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

function SaveBadge({ status }: { status: 'idle' | 'pending' | 'success' | 'error' }) {
  if (status === 'idle') return null
  const base = 'inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full'
  if (status === 'pending') {
    return (
      <span className={`${base} bg-slate-100 text-slate-600`}>
        <span className="size-1.5 rounded-full bg-slate-400 animate-pulse" />
        Zapisywanie
      </span>
    )
  }
  if (status === 'success') {
    return (
      <span className={`${base} bg-emerald-50 text-emerald-700`}>
        <span className="size-1.5 rounded-full bg-emerald-500" />
        Zapisano
      </span>
    )
  }
  return (
    <span className={`${base} bg-red-50 text-red-700`}>
      <span className="size-1.5 rounded-full bg-red-500" />
      Błąd zapisu
    </span>
  )
}

function clampWidth(w: number | undefined): number {
  if (!w || w < 1) return 12
  if (w > 12) return 12
  return w
}
